import type { UserTask, UserTaskContext } from "@/types/userTasks.ts";

/**
 * The longest note the server takes (`setContextReq`), held by every field
 * that writes one — the editor and the hand-off dialog on both platforms —
 * so a note too long to save is stopped at the keyboard rather than at the
 * request.
 */
export const MAX_CONTEXT_CHARS = 4000;

/**
 * Whether a card offers a note: a task still to do. The user tells okthx where
 * it stands — "called the vet, they call back Thursday" — and okthx keeps it.
 */
export function takesNote(task: Pick<UserTask, "status">): boolean {
  return task.status === "open" || task.status === "later";
}

/**
 * Where a task stands as it is shown: without images. It is written from other
 * people's mail, and an image there would load from wherever they point it.
 */
export function withoutImages(markdown: string): string {
  // Inline `![a](url)`, reference `![a][ref]` and shortcut `![a]` images alike.
  return markdown.replace(/!\[[^\]]*\](\([^)]*\)|\[[^\]]*\])?/g, "").trim();
}

/**
 * The note's words with its links unmade: "See the thread for the rest" reads
 * as the sentence it is, with nothing in it to press.
 *
 * For a card in a list, where the card itself is the one control and a link
 * inside it would be a second one that opens somewhere else. The task's page
 * and the triage card's back keep the links, since there the note is read in
 * full and is not sitting on a control.
 */
export function withoutLinks(markdown: string) {
  // Inline `[words](url)` and reference `[words][ref]` links keep their words.
  // A URL may hold one pair of parentheses of its own, as a wiki page's does,
  // so the inline form matches a balanced pair inside the outer one.
  return markdown.replace(/\[([^\]]*)\](\((?:[^()]|\([^()]*\))*\)|\[[^\]]*\])/g, "$1");
}

/**
 * The note button's words: Edit where the card is showing a note, Add where it
 * is not — whoever wrote it.
 *
 * Whoever wrote it, because whose words they are is not a distinction the
 * reader can see — a card plainly showing okthx's summary would otherwise
 * offer to Add another — and the editor opens on the same text the card is
 * showing, so the two answer one question. Editing okthx's summary makes it
 * the reader's, which is what editing it means.
 */
export function noteButtonLabel(context: Pick<UserTaskContext, "text"> | null): string {
  return context?.text?.trim() ? "Edit note" : "Add a note";
}
